import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LOGIN } from "@/constants/testIds";

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Login() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const buttonRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCredentialResponse = useCallback(
    async (response) => {
      if (!response?.credential) return;
      setLoading(true);
      setError("");
      try {
        await api.post("/auth/google", { credential: response.credential });
        await refresh();
        navigate("/dashboard", { replace: true });
      } catch (requestError) {
        setError(
          requestError.response?.data?.detail || "Google login failed. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [navigate, refresh]
  );

  useEffect(() => {
    if (!googleClientId) {
      setError("Google login is not configured.");
      return undefined;
    }

    const initialize = () => {
      if (!window.google?.accounts?.id || !buttonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
        ux_mode: "popup",
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: "outline",
        size: "large",
        text: "continue_with",
        width: 360,
      });
    };

    if (window.google?.accounts?.id) {
      initialize();
    } else {
      const existing = document.getElementById("google-identity-services");
      if (existing) {
        existing.addEventListener("load", initialize, { once: true });
      } else {
        const script = document.createElement("script");
        script.id = "google-identity-services";
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = initialize;
        script.onerror = () => setError("Unable to load Google login.");
        document.head.appendChild(script);
      }
    }

    return () => {
      const script = document.getElementById("google-identity-services");
      script?.removeEventListener("load", initialize);
    };
  }, [handleCredentialResponse]);

  return (
    <div className="min-h-screen bg-[#FBFBF9] text-zinc-950 flex flex-col">
      <div className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-zinc-950 flex items-center justify-center">
              <span className="font-serif text-[#FBFBF9] text-lg leading-none">Æ</span>
            </div>
            <span className="font-serif text-[15px] tracking-tight">AI Photo & Content Studio</span>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
            Product · Model · Editorial
          </span>
        </div>
      </div>

      <main data-testid={LOGIN.landingHero} className="flex-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
              Photorealistic · Editorial · One photo to a full shoot
            </p>
            <h1 className="font-serif text-[2.75rem] sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-zinc-950">
              Turn one product photo
              <span className="block italic text-zinc-500">into a pro photoshoot.</span>
            </h1>
            <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-zinc-600">
              Upload fashion, drinks, food, cosmetics, electronics, or accessories.
              Create consistent product photos with or without a virtual model.
            </p>

            <div className="mt-10 min-h-[54px]">
              <div ref={buttonRef} data-testid={LOGIN.googleSignInButton} />
              {loading && <p className="mt-3 text-sm text-zinc-500">Signing you in…</p>}
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden bg-zinc-100">
                <img
                  src="https://images.unsplash.com/photo-1654512697681-8434b50096dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwc3R1ZGlvJTIwbW9kZWwlMjBwb3J0cmFpdCUyMGxvb2tib29rfGVufDB8fHx8MTc4ODgyMzQwN3ww&ixlib=rb-4.1.0&q=85"
                  alt="AI product photography editorial"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-40 aspect-square overflow-hidden bg-zinc-100 border-8 border-[#FBFBF9]">
                <img
                  src="https://images.unsplash.com/photo-1517502166878-35c93a0072f0?auto=format&fit=crop&w=900&q=85"
                  alt="Garment reference"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="absolute top-4 right-4 font-mono text-[9px] uppercase tracking-[0.25em] bg-[#FBFBF9]/90 px-2 py-1 text-zinc-800">
                Rendered · Studio
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex justify-between text-[11px] text-zinc-500 font-mono uppercase tracking-[0.2em]">
          <span>© AI Photo & Content Studio</span>
          <span>Free · No Card Required</span>
        </div>
      </footer>
    </div>
  );
}
